/* eslint-disable import/no-cycle */
/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import { useCallback } from 'react';
import isObject from 'lodash/isObject';
import { FormFieldT, FormItemT, FormSectionT } from '../types';
import { isFieldSchema, isSectionSchema } from '../utils';
import { useStoreActions, useStore } from '../../../../models/hooks';

function deepClone<T>(obj: T): T {
  if (obj === null || !isObject(obj)) {
    return obj;
  }
  if (Array.isArray(obj)) {
    const copy = [] as any[];
    for (let i = 0; i < obj.length; /* eslint-disable-line no-plusplus */ i++) {
      copy[i] = deepClone(obj[i]);
    }
    return (copy as unknown) as T;
  }
  const copy = {} as { [key: string]: any };
  /* eslint-disable-next-line */
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      copy[key] = deepClone(obj[key]);
    }
  }
  return copy as T;
}

function isPlainObject(item: unknown): item is Record<string, unknown> {
  return item !== null && isObject(item) && item?.constructor === Object;
}

function deepMerge(target: any, source: any): any {
  // If either isn't an object, source wins (unless source is undefined)
  if (!isPlainObject(target) || !isPlainObject(source)) {
    return source === undefined ? target : source;
  }

  Object.keys(source).forEach((key) => {
    const sourceValue = source[key];
    const targetValue = target[key];

    if (isPlainObject(sourceValue) && isPlainObject(targetValue)) {
      // Recursively merge nested objects
      target[key] = deepMerge({ ...targetValue }, sourceValue);
    } else if (sourceValue !== undefined) {
      // Primitive overwrite or new key addition
      target[key] = deepClone(sourceValue);
    }
  });

  return target;
}

export type FieldUpdatePayload = Partial<Omit<FormFieldT, 'id' | 'inputType' | 'followUpQuestionList'>> & {
  config?: Record<string, any>;
  followUpQuestionList?: (FormFieldT | FormSectionT)[] | null;
  [key: string]: any;
};

export type SectionUpdatePayload = Partial<Omit<FormSectionT, 'id' | 'type' | 'fields'>> & {
  [key: string]: any;
};

export type ItemUpdatePayload = FieldUpdatePayload | SectionUpdatePayload;

interface SpecificYesNoFieldSchema extends Omit<FormFieldT, 'followUpQuestionList' | 'inputType'> {
  inputType: 'yesNo';
  followUpQuestionList?: (FormFieldT | FormSectionT)[] | null;
}

function recursiveItemUpdate(items: FormItemT[], targetId: string, updates: ItemUpdatePayload): FormItemT[] {
  let hasChanged = false;

  const newItems = items.map((item) => {
    // 1. Check if this is the target
    if (item.id === targetId) {
      hasChanged = true;
      // Merge the item with updates.
      // We clone the original and merge updates on top.
      const updatedItem = deepMerge(deepClone(item), updates);

      // 2. Handle Yes/No logic specifically
      if (isFieldSchema(updatedItem) && updatedItem.inputType === 'yesNo') {
        const val = updatedItem.config?.value;
        const isNo = val === 'No' || val === false;
        const isYes = val === 'Yes' || val === true;

        if (isNo) {
          updatedItem.followUpQuestionList = null;
        } else if (isYes && updatedItem.followUpQuestionList === null) {
          updatedItem.followUpQuestionList = [];
        }
      }
      return updatedItem;
    }

    // 3. Handle Recursion for Sections
    if (isSectionSchema(item)) {
      const updatedFields = recursiveItemUpdate(item.fields, targetId, updates);
      if (updatedFields !== item.fields) {
        hasChanged = true;
        return { ...item, fields: updatedFields };
      }
    }

    // 4. Handle Recursion for Yes/No follow-ups
    if (isFieldSchema(item) && item.inputType === 'yesNo' && item.followUpQuestionList) {
      const updatedFollowUps = recursiveItemUpdate(item.followUpQuestionList, targetId, updates);
      if (updatedFollowUps !== item.followUpQuestionList) {
        hasChanged = true;
        return { ...item, followUpQuestionList: updatedFollowUps };
      }
    }

    return item;
  });

  return hasChanged ? newItems : items;
}

export function useUpdateFormItem() {
  const setSchema = useStoreActions((actions) => actions.formBuilder.setSchema);
  const storeInstance = useStore();

  const updateItemProperties = useCallback(
    (targetId: string, updates: ItemUpdatePayload): void => {
      const currentSchema = storeInstance.getState().formBuilder.formSchema;

      if (!currentSchema) return;

      const newSchema = recursiveItemUpdate(currentSchema, targetId, updates);

      if (newSchema !== currentSchema) {
        setSchema(newSchema);
      }
    },
    [setSchema, storeInstance],
  );

  return updateItemProperties;
}
